'use client'

import { Languages } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import {
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { buildLocalizedPath, stripPathForLocaleSwitch } from '@/modules/languages/lib/localizedPath'
import { useLocaleAlternates } from '@/modules/languages/lib/localeAlternates'
import { flagUrl, getCatalogEntry } from '@/modules/languages/lib/catalog'

import type { PublicLanguageOption } from './LanguageSwitcher.client'

export type LanguageFlagMenuProps = {
  defaultLocale: string
  currentLocale: string
  languages: PublicLanguageOption[]
  showFlags?: boolean
}

/**
 * ITEM 8 — idiomas disponíveis NA PÁGINA ATUAL (post/página → só os traduzidos; chrome → live).
 * Enquanto carrega (`null`) o seletor mostra todos os ativos (sem flash); depois filtra. O default
 * é sempre mantido por `buildOptions`. Falha de rede → mantém todos (degrada para o comportamento atual).
 */
function useAvailableLocaleSet(): Set<string> | null {
  const pathname = usePathname() || '/'
  const [set, setSet] = useState<Set<string> | null>(null)
  useEffect(() => {
    let cancelled = false
    setSet(null)
    fetch(`/api/public/doc-locales?path=${encodeURIComponent(pathname)}`, {
      credentials: 'same-origin',
    })
      .then((r) => r.json())
      .then((d: { locales?: string[] }) => {
        if (!cancelled) setSet(new Set(d.locales ?? []))
      })
      .catch(() => {
        if (!cancelled) setSet(null)
      })
    return () => {
      cancelled = true
    }
  }, [pathname])
  return set
}

/**
 * Preserva a query string (`?q=…` na busca, filtros, paginação) ao trocar de idioma. Lê em efeito
 * (NÃO `useSearchParams`, que optaria páginas estáticas por render dinâmico) e re-sincroniza por rota.
 */
function useLocaleSwitchSearch(pathname: string): string {
  const [search, setSearch] = useState('')
  useEffect(() => {
    setSearch(typeof window !== 'undefined' ? window.location.search : '')
  }, [pathname])
  return search
}

/** Português (locale padrão, sem prefixo de URL) sempre encabeça a lista. */
function buildOptions(
  defaultLocale: string,
  languages: PublicLanguageOption[],
): PublicLanguageOption[] {
  const entry = getCatalogEntry(defaultLocale)
  return [
    { code: defaultLocale, label: entry?.nativeName ?? 'Português', flag: entry?.flag ?? 'br' },
    ...languages.filter((l) => l.code !== defaultLocale),
  ]
}

function FlagImg({ flag, size = 20 }: { flag: string; size?: number }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={flagUrl({ flag }, 'w20')}
      alt=""
      width={size}
      height={Math.round((size * 3) / 4)}
      className="shrink-0 rounded-[2px] object-cover"
      loading="lazy"
      decoding="async"
    />
  )
}

/* ─── Desktop — submenu lateral (flyout), abaixo de "Moeda" ──────────────────────
 * Padrão comum: o item "Idioma" (globo + seta ›) abre um painel ao lado direito
 * do menu principal, SEM overlay escuro e com o menu pai visível. O `DropdownMenuSub`
 * é estrutura ARIA válida (menuitem → submenu), ao contrário de um <div role="dialog">
 * aninhado no role="menu". O `DropdownMenuPortal` monta o painel no <body>, fora do
 * `overflow-hidden` do menu pai (sem recorte). */

export function LanguageFlagMenu({
  defaultLocale,
  currentLocale,
  languages,
  showFlags = true,
}: LanguageFlagMenuProps) {
  const t = useTranslations('LanguageSwitcher')
  const pathname = usePathname() || '/'
  const basePath = stripPathForLocaleSwitch(pathname)
  const search = useLocaleSwitchSearch(pathname)
  // Caminho REAL do idioma-alvo via alternates do <head> (slug traduzido por idioma); sem
  // alternate → troca de prefixo (comportamento antigo, correto p/ docs sem tradução).
  const alternateFor = useLocaleAlternates(pathname)
  const availSet = useAvailableLocaleSet()
  const options = buildOptions(
    defaultLocale,
    availSet ? languages.filter((l) => availSet.has(l.code)) : languages,
  )
  if (options.length <= 1) return null
  const current = options.find((l) => l.code === currentLocale) ?? options[0]
  const linkFor = (code: string) =>
    (alternateFor(code) ?? buildLocalizedPath(basePath, code, defaultLocale)) + search

  return (
    <DropdownMenuSub>
      {/* SubTrigger já adiciona o ChevronRight (›) ao final automaticamente. */}
      <DropdownMenuSubTrigger className="min-w-[14rem] cursor-pointer gap-3 py-2.5 pl-2 pr-2">
        {showFlags ? (
          <FlagImg flag={current.flag} />
        ) : (
          <Languages className="size-[18px] shrink-0 text-foreground" aria-hidden />
        )}
        <span className="text-sm font-semibold text-foreground">{t('label')}</span>
        <span className="ml-auto text-xs font-medium text-muted-foreground">{current.label}</span>
      </DropdownMenuSubTrigger>

      <DropdownMenuPortal>
        <DropdownMenuSubContent className="max-h-[310px] overflow-y-auto">
          {options.map((lang) => {
            const active = lang.code === currentLocale
            return (
              <DropdownMenuItem
                key={lang.code}
                asChild
                className={active ? 'font-semibold' : undefined}
              >
                {/* FIX (2026-06-26): `<a>` (navegação HARD), NÃO o `<Link>` do Next. `/es`/`/en` são
                    rewrite do middleware (proxy.ts), não rota real do App Router → a soft-nav do `<Link>`
                    não dispara o middleware e NÃO troca o locale (fica no PT). Hard nav recarrega e o
                    middleware serve o HTML localizado. Espelha o seletor mobile e o LanguageSwitcher.
                    Doc: docs/_arquivo-portalcripto/AUDITORIA_SELETOR_IDIOMA_LINK_SOFTNAV_2026_06_26.md */}
                <a href={linkFor(lang.code)} aria-current={active ? 'true' : undefined}>
                  {showFlags ? (
                    <FlagImg flag={lang.flag} />
                  ) : (
                    <Languages className="size-[18px] shrink-0" aria-hidden />
                  )}
                  <span className="min-w-0 truncate">{lang.label}</span>
                </a>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

/* ─── Mobile (Sheet) — modal Dialog ──────────────────────────────────────────────
 * No menu mobile (um Sheet, ocupa a tela toda), o flyout lateral não se aplica; aqui
 * usamos o Dialog do Radix, que porta o overlay/conteúdo para o <body> (sem recorte).
 * (Não é o <div role="dialog"> cru aninhado no menu — é um Dialog portado, fora do Sheet.) */

function LanguageFlagModal({
  open,
  onOpenChange,
  options,
  currentLocale,
  defaultLocale,
  showFlags,
  title,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  options: PublicLanguageOption[]
  currentLocale: string
  defaultLocale: string
  showFlags: boolean
  title: string
}) {
  const pathname = usePathname() || '/'
  const basePath = stripPathForLocaleSwitch(pathname)
  const search = useLocaleSwitchSearch(pathname)
  // Mesma fonte dos demais seletores: alternates do <head> → slug traduzido correto por idioma.
  const alternateFor = useLocaleAlternates(pathname)
  const linkFor = (code: string) =>
    (alternateFor(code) ?? buildLocalizedPath(basePath, code, defaultLocale)) + search
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* z-[9999]: acima do rodapé (z-index 1000, CSS em base64 no Head/Footer Code) e abaixo do banner da Sevio. */}
      <DialogContent
        className="z-[9999] max-h-[70vh] gap-2 overflow-auto p-3 sm:max-w-sm"
        overlayClassName="z-[9999]"
      >
        <DialogTitle className="pr-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </DialogTitle>
        <ul className="flex flex-col">
          {options.map((lang) => {
            const active = lang.code === currentLocale
            return (
              <li key={lang.code}>
                <a
                  href={linkFor(lang.code)}
                  aria-current={active ? 'true' : undefined}
                  className={
                    'flex items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted ' +
                    (active ? 'font-semibold text-foreground' : 'text-foreground/90')
                  }
                >
                  {showFlags ? (
                    <FlagImg flag={lang.flag} />
                  ) : (
                    <Languages className="size-[18px] shrink-0" aria-hidden />
                  )}
                  <span className="min-w-0 truncate">{lang.label}</span>
                </a>
              </li>
            )
          })}
        </ul>
      </DialogContent>
    </Dialog>
  )
}

export function LanguageFlagToggleSheet({
  defaultLocale,
  currentLocale,
  languages,
  showFlags = true,
}: LanguageFlagMenuProps) {
  const t = useTranslations('LanguageSwitcher')
  const [open, setOpen] = useState(false)
  const availSet = useAvailableLocaleSet()
  const options = buildOptions(
    defaultLocale,
    availSet ? languages.filter((l) => availSet.has(l.code)) : languages,
  )
  if (options.length <= 1) return null
  const current = options.find((l) => l.code === currentLocale) ?? options[0]

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between gap-3 rounded-lg border-2 border-foreground bg-background px-3 py-2.5 text-left transition-[background-color] hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span className="flex items-center gap-2.5">
          {showFlags ? (
            <FlagImg flag={current.flag} />
          ) : (
            <Languages className="size-[18px] shrink-0 text-foreground" aria-hidden />
          )}
          <span className="text-sm font-semibold text-foreground">{t('label')}</span>
        </span>
        <span className="text-xs font-medium text-muted-foreground">{current.label}</span>
      </button>

      <LanguageFlagModal
        open={open}
        onOpenChange={setOpen}
        options={options}
        currentLocale={currentLocale}
        defaultLocale={defaultLocale}
        showFlags={showFlags}
        title={t('label')}
      />
    </>
  )
}
